import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import { useActiveContextReady } from './useActiveContextReady';
import { apiErrorMessage } from '../utils/apiError';
import { config } from '../config';

/** Router advertisers and DHCP servers a gateway hears (gateway#72). */
export type ConfigSourceKind = 'ra' | 'dhcp' | 'dhcpv6';

export interface RouterOffer {
  default_router: boolean;
  preference: string;
  managed: boolean;
  other_config: boolean;
  proxy: boolean;
  prefixes: string[];
  dns_servers: string[];
}

export interface ConfigSource {
  kind: ConfigSourceKind;
  /** Advertiser MAC, DHCP server identifier, or DHCPv6 DUID. */
  identity: string;
  via: string;
  mac: string;
  address: string;
  relay: string | null;
  relayLeg: boolean;
  router: RouterOffer | null;
  firstSeen: string | null;
  lastHeard: string | null;
  /** Set once the gateway stopped reporting it. */
  goneAt: string | null;
  /** Counted by the rule: a default-router candidate, or a server answering this link. */
  competes: boolean;
  expected: boolean;
  location: { deviceId: string; deviceName: string; interface: string } | null;
}

export interface ProbeServer {
  kind: ConfigSourceKind;
  identity: string;
  mac: string;
  address: string;
}

export interface ProbeOutcome {
  sent: boolean;
  message: string;
  servers: ProbeServer[];
}

export interface ConfigSegment {
  interface: string;
  vlan: number;
  name: string;
  listening: boolean;
  sources: ConfigSource[];
  expectedKinds: ConfigSourceKind[];
  probe: { enabledAt: string; lastProbedAt: string | null; lastOutcome: ProbeOutcome | null } | null;
  incidents: { id: string; kind: ConfigSourceKind; title: string; severity: string; status: string }[];
}

const key = (gatewayId: string | undefined) => ['gatewayConfigSources', gatewayId];

/** The config-sources API base for a gateway, which mutations need to exist. */
function base(gatewayId: string | undefined): string {
  if (!gatewayId) throw new Error('Gateway ID is required');
  return `${config.backendApi}/api/gateways/${gatewayId}/config-sources`;
}

export function useGatewayConfigSources(gatewayId: string | undefined) {
  const contextReady = useActiveContextReady();
  return useQuery<ConfigSegment[]>({
    queryKey: key(gatewayId),
    queryFn: async () => {
      if (!gatewayId) throw new Error('Gateway ID is required');
      const response = await axios.get<{ segments: ConfigSegment[] }>(
        `${config.backendApi}/api/gateways/${gatewayId}/config-sources`,
        { withCredentials: true },
      );
      return response.data.segments;
    },
    enabled: !!gatewayId && contextReady,
  });
}

interface SegmentRef {
  interface: string;
  vlan: number;
}

/** Mark a segment's current sources of one kind as expected, or clear the set. */
export function useSetExpectedConfigSources(gatewayId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      body: SegmentRef & { kind: ConfigSourceKind; expected: boolean; identity?: string; via?: string },
    ) => {
      await axios.put(`${base(gatewayId)}/expected`, body, {
        withCredentials: true,
      });
    },
    onSuccess: (_data, body) => {
      void queryClient.invalidateQueries({ queryKey: key(gatewayId) });
      toast.success(body.expected ? 'Marked as expected' : 'Expected set cleared');
    },
    onError: (error) => {
      toast.error(apiErrorMessage(error, 'Could not change the expected set'));
    },
  });
}

/** Opt a segment into the periodic DHCP probe, or out. */
export function useSetDhcpProbe(gatewayId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SegmentRef & { enabled: boolean }) => {
      await axios.put(`${base(gatewayId)}/probe`, body, {
        withCredentials: true,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: key(gatewayId) });
    },
    onError: (error) => {
      toast.error(apiErrorMessage(error, 'Could not change the DHCP probe'));
    },
  });
}

/** Probe a segment for DHCP servers now. */
export function useRunDhcpProbe(gatewayId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SegmentRef) => {
      const response = await axios.post<ProbeOutcome>(
        `${base(gatewayId)}/probe/run`,
        body,
        { withCredentials: true },
      );
      return response.data;
    },
    onSuccess: (outcome) => {
      void queryClient.invalidateQueries({ queryKey: key(gatewayId) });
      if (!outcome.sent) {
        toast.error(`Probe not sent: ${outcome.message}`);
      } else {
        const n = outcome.servers.length;
        toast.success(n === 0 ? 'No DHCP server answered' : `${String(n)} DHCP server${n === 1 ? '' : 's'} answered`);
      }
    },
    onError: (error) => {
      toast.error(apiErrorMessage(error, 'Could not run the DHCP probe'));
    },
  });
}
